import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, MapPin, Phone, Send, ShieldCheck, User } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LEGAL, SUPPORT_EMAIL } from "@/content/info";
import { metaContext, track } from "@/lib/meta";

const field =
  "h-11 bg-zinc-900/60 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-indigo-500/40";

export const InfoShell = ({ eyebrow, title, lead, children, testid }) => (
  <div className="min-h-screen nv-grid-bg" data-testid={testid}>
    <header className="sticky top-0 z-30 nv-glass border-b border-zinc-800/70">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5" data-testid="info-home-link">
          <Logo size={28} withWordmark />
        </Link>
        <div className="flex items-center gap-2">
          <ThemeToggle className="hidden sm:inline-flex" />
          <Link
            to="/login"
            data-testid="info-signin-link"
            className="h-9 px-4 inline-flex items-center rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold transition-colors"
          >
            Sign in
          </Link>
        </div>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-indigo-400 mb-8">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to home
      </Link>
      {eyebrow && <p className="text-[11px] font-num uppercase tracking-[0.24em] text-indigo-400 mb-3">{eyebrow}</p>}
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">{title}</h1>
      {lead && <p className="mt-4 text-base md:text-lg text-zinc-400 leading-relaxed">{lead}</p>}
      <div className="mt-10 space-y-8">{children}</div>
    </main>

    <InfoFooter />
  </div>
);

export const InfoFooter = () => (
  <footer className="border-t border-zinc-800/70 mt-8">
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
      <div className="flex items-center gap-2.5">
        <Logo size={24} />
        <span className="text-xs text-zinc-500">© {new Date().getFullYear()} Propojo. All rights reserved.</span>
      </div>
      <nav className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-zinc-500" data-testid="info-footer-nav">
        <Link to="/about" className="hover:text-indigo-400" data-testid="footer-about">About us</Link>
        <Link to="/contact" className="hover:text-indigo-400" data-testid="footer-contact">Contact</Link>
        <Link to="/privacy" className="hover:text-indigo-400" data-testid="footer-privacy">Privacy policy</Link>
        <Link to="/terms" className="hover:text-indigo-400" data-testid="footer-terms">Terms of service</Link>
        <Link to="/refund-policy" className="hover:text-indigo-400" data-testid="footer-refund">Refund policy</Link>
      </nav>
    </div>
  </footer>
);

const Section = ({ heading, body }) => (
  <section>
    <h2 className="text-base md:text-lg font-semibold text-zinc-100">{heading}</h2>
    {body.map((p, i) => (
      <p key={i} className="mt-2.5 text-sm text-zinc-400 leading-relaxed">
        {p}
      </p>
    ))}
  </section>
);

const LegalPage = ({ slug }) => {
  const doc = LEGAL[slug];
  return (
    <InfoShell eyebrow={doc.eyebrow} title={doc.title} lead={doc.lead} testid={`page-${slug}`}>
      <p className="text-xs text-zinc-500 font-num">Last updated: {doc.updated}</p>
      {doc.sections.map((s) => (
        <Section key={s.heading} heading={s.heading} body={s.body} />
      ))}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 flex items-start gap-3">
        <ShieldCheck className="w-4 h-4 text-indigo-400 mt-0.5 shrink-0" />
        <p className="text-sm text-zinc-400">
          Questions about this document? Write to{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-400 hover:text-indigo-300">
            {SUPPORT_EMAIL}
          </a>{" "}
          and a human will reply.
        </p>
      </div>
    </InfoShell>
  );
};

export const Privacy = () => <LegalPage slug="privacy" />;
export const Terms = () => <LegalPage slug="terms" />;
export const Refund = () => <LegalPage slug="refund" />;

export const About = () => (
  <InfoShell
    eyebrow="About us"
    title="Proposals should not cost you a weekend"
    lead="Propojo turns a rough brief or a messy sales call into a branded, client-ready proposal in minutes — so agencies and freelancers spend their time winning work, not formatting documents."
    testid="page-about"
  >
    {LEGAL.about.sections.map((s) => (
      <Section key={s.heading} heading={s.heading} body={s.body} />
    ))}
    <div className="grid sm:grid-cols-3 gap-4 pt-2">
      {[
        { k: "Minutes", v: "From brief to sendable proposal" },
        { k: "Your brand", v: "Fonts, colours and logo on every page" },
        { k: "Signed online", v: "Clients accept without printing anything" },
      ].map((s) => (
        <div key={s.k} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
          <p className="text-sm font-semibold text-indigo-400">{s.k}</p>
          <p className="text-sm text-zinc-400 mt-1.5">{s.v}</p>
        </div>
      ))}
    </div>
    <Link
      to="/signup"
      data-testid="about-cta"
      className="inline-flex items-center h-11 px-6 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold"
    >
      Create your first proposal
    </Link>
  </InfoShell>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const eventId = `lead_${Date.now()}`;
      await api.post("/contact", { ...form, meta: metaContext(eventId) });
      track("Lead", { content_name: form.subject, currency: "INR", value: 0 },
            { eventId, email: form.email, browserOnly: true });
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <InfoShell
      eyebrow="Contact us"
      title="Talk to the people who build Propojo"
      lead="Sales question, bug, billing, or a feature you wish existed — send it over. We read everything."
      testid="page-contact"
    >
      <div className="grid md:grid-cols-[1fr_260px] gap-8 items-start">
        {sent ? (
          <div data-testid="contact-sent" className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-6">
            <p className="text-base font-semibold text-emerald-300">Message received</p>
            <p className="text-sm text-zinc-300 mt-2">
              Thanks {form.name.split(" ")[0] || "there"} — we've logged it and will reply to {form.email} shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input data-testid="contact-name" className={field} required placeholder="Your name" value={form.name} onChange={set("name")} />
              <Input data-testid="contact-email" className={field} type="email" required placeholder="you@company.com" value={form.email} onChange={set("email")} />
            </div>
            <Input data-testid="contact-subject" className={field} required placeholder="Subject" value={form.subject} onChange={set("subject")} />
            <textarea
              data-testid="contact-message"
              required
              minLength={10}
              rows={6}
              placeholder="How can we help?"
              value={form.message}
              onChange={set("message")}
              className="w-full rounded-lg bg-zinc-900/60 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 text-sm p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
            />
            {error && (
              <p data-testid="contact-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
            <Button data-testid="contact-submit" type="submit" disabled={busy} className="h-11 px-6 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send message <Send className="w-4 h-4 ml-1.5" /></>}
            </Button>
          </form>
        )}

        <aside className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 space-y-4 w-full">
          <div className="flex items-start gap-2.5">
            <Mail className="w-4 h-4 text-indigo-400 mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-500">Email</p>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sm text-zinc-200 hover:text-indigo-400 break-all">
                {SUPPORT_EMAIL}
              </a>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <User className="w-4 h-4 text-indigo-400 mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-500">Contact person</p>
              <p className="text-sm text-zinc-200" data-testid="contact-person">Propojo Team</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <Phone className="w-4 h-4 text-indigo-400 mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-500">Phone</p>
              <a href="tel:+919329793939" className="text-sm text-zinc-200 hover:text-indigo-400" data-testid="contact-phone">
                9329793939
              </a>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <MapPin className="w-4 h-4 text-indigo-400 mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-500">Address</p>
              <p className="text-sm text-zinc-200" data-testid="contact-address">
                Bhopal, Madhya Pradesh
              </p>
            </div>
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed">
            Typical reply time is one business day. Billing and refund requests are handled by the same inbox.
          </p>
        </aside>
      </div>
    </InfoShell>
  );
};
